import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';

import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { useRedeemMagicLinkMutation } from '@/lib/api/auth';
import { useAuth } from '@/lib/auth/store';

/**
 * /viewer/redeem — the landing route for client-viewer invitation emails
 * (Tier 2). The API builds the emailed URL as
 * `<app_base_url>/viewer/redeem#<token>` — the token rides the URL
 * FRAGMENT so it never reaches server logs. This route reads the hash,
 * redeems it, stores the grant id (sent as X-Viewer-Grant-Id on every
 * subsequent request), and lands the viewer on the app root.
 *
 * Tokens are single-use with a 15-minute TTL; on failure we show the
 * error with a pointer to the manual-paste fallback on /login.
 */
export const Route = createFileRoute('/viewer/redeem')({
  component: ViewerRedeemScreen,
});

function ViewerRedeemScreen() {
  const navigate = useNavigate();
  const redeem = useRedeemMagicLinkMutation();
  const setViewerGrant = useAuth((s) => s.setViewerGrant);
  const [noToken, setNoToken] = useState(false);
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    const token = window.location.hash.replace(/^#/, '').trim();
    if (!token) {
      setNoToken(true);
      return;
    }
    // Clear the fragment so the token doesn't linger in the address bar /
    // browser history once consumed.
    window.history.replaceState(null, '', window.location.pathname);
    redeem.mutate(
      { token },
      {
        onSuccess: (result) => {
          if (result.grant_id) setViewerGrant(result.grant_id);
          void navigate({ to: '/' });
        },
      },
    );
  }, [redeem, setViewerGrant, navigate]);

  return (
    <div className="grid min-h-screen place-items-center bg-paper-50 p-6">
      <div className="w-full max-w-md space-y-4 rounded-modal bg-paper-0 p-8 shadow-modal">
        <h1 className="text-h2 font-semibold text-ink-900">Client viewer access</h1>
        {noToken ? (
          <p className="text-body-sm text-ink-700">
            This link is missing its access token. Open the invitation link exactly as it appears in
            your email, or paste the token manually on the{' '}
            <Link to="/login" className="font-medium text-accent-amber-700 underline">
              sign-in page
            </Link>
            .
          </p>
        ) : redeem.isError ? (
          <div className="space-y-3">
            <ErrorMessage error={redeem.error} />
            <p className="text-body-sm text-ink-700">
              Invitation links are single-use and expire after 15 minutes. Ask your Avero contact to
              reissue the invitation, or paste a fresh token on the{' '}
              <Link to="/login" className="font-medium text-accent-amber-700 underline">
                sign-in page
              </Link>
              .
            </p>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-body-sm text-ink-700">
            <Spinner /> Verifying your invitation…
          </div>
        )}
      </div>
    </div>
  );
}
